import axios from 'axios';

// Get backend URL and trim any potential spaces to prevent URL encoding issues
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim();
const API = `${BACKEND_URL}/api`.trim();

// Ensure URLs are properly formatted as absolute URLs
const ensureAbsoluteURL = (endpoint) => {
  if (!endpoint) return endpoint;
  
  // If it's already absolute, return as is
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }
  
  // If it starts with /api, use the backend URL directly
  if (endpoint.startsWith('/api')) {
    return `${BACKEND_URL}${endpoint}`;
  }
  
  // If it's a relative endpoint like '/posts', prepend the API base
  if (endpoint.startsWith('/')) {
    return `${API}${endpoint}`;
  }
  
  // For bare endpoints like 'posts', prepend API base with slash
  return `${API}/${endpoint}`;
};

// Utility function to ensure clean URLs (removes any spaces that could cause ? encoding)
const cleanURL = (url) => {
  if (!url) return url;
  
  let cleaned = url.trim().replace(/\s+/g, '');
  
  // Fix the specific issue: URLs like "https://ismpg.onrender.com/?/api/art"
  // This happens when current route gets mixed with API base URL
  if (cleaned.includes('/?/api/')) {
    cleaned = cleaned.replace('/?/api/', '/api/');
    console.warn('🔧 Fixed malformed URL with /?/api/ pattern:', {
      original: url,
      fixed: cleaned
    });
  }
  
  // Also fix cases where the URL might have just /? pattern
  if (cleaned.includes('/?')) {
    cleaned = cleaned.replace('/?', '/');
    console.warn('🔧 Fixed URL with /? pattern:', {
      original: url,
      fixed: cleaned
    });
  }
  
  if (url !== cleaned && (url?.includes(' ') || url?.includes('/?'))) {
    console.warn('⚠️ URL contained issues (spaces or malformed patterns), cleaned automatically:', { 
      original: url, 
      cleaned 
    });
  }
  
  return cleaned;
};

// Create axios instance with robust URL handling
const axiosInstance = axios.create();
axiosInstance.interceptors.request.use((config) => {
  const originalUrl = config.url;
  
  // First ensure the URL is absolute
  config.url = ensureAbsoluteURL(config.url);
  
  // Then clean any malformed patterns
  config.url = cleanURL(config.url);
  
  // Debug logging for any URL transformations
  if (originalUrl !== config.url) {
    console.log('🔧 URL transformed:', {
      original: originalUrl,
      transformed: config.url
    });
  }
  
  // Alert for the specific malformed pattern the user reported
  if (config.url?.includes('/?/api/') || originalUrl?.includes('/?/api/')) {
    console.error('🚨 CAUGHT MALFORMED URL PATTERN /?/api/ - FIXED:', {
      originalUrl,
      fixedUrl: config.url,
      currentLocation: window.location.href
    });
  }
  
  return config;
});

// Posts API
export const postsAPI = {
  getAll: () => axiosInstance.get('posts'),
  create: (postData, headers) => axiosInstance.post('posts', postData, { headers }),
  delete: (postId, headers) => axiosInstance.delete(`posts/${postId}`, { headers })
};

// Members API
export const membersAPI = {
  getAll: () => axiosInstance.get('members'),
  create: (memberData, headers) => axiosInstance.post('members', memberData, { headers }),
  update: (memberId, memberData, headers) => axiosInstance.put(`members/${memberId}`, memberData, { headers }),
  delete: (memberId, headers) => axiosInstance.delete(`members/${memberId}`, { headers })
};

// About API
export const aboutAPI = {
  get: () => axiosInstance.get('about'),
  update: (aboutData, headers) => axiosInstance.put('about', aboutData, { headers })
};

// Contact API
export const contactAPI = {
  getAll: () => axiosInstance.get('contacts'),
  create: (contactData, headers) => axiosInstance.post('contacts', contactData, { headers }),
  delete: (contactId, headers) => axiosInstance.delete(`contacts/${contactId}`, { headers })
};

// Credits API
export const creditsAPI = {
  getAll: () => axiosInstance.get('credits'),
  create: (creditData, headers) => axiosInstance.post('credits', creditData, { headers }),
  update: (creditId, creditData, headers) => axiosInstance.put(`credits/${creditId}`, creditData, { headers }),
  delete: (creditId, headers) => axiosInstance.delete(`credits/${creditId}`, { headers })
};

// Carousel API
export const carouselAPI = {
  getAll: () => axiosInstance.get('carousel'),
  create: (carouselData, headers) => axiosInstance.post('carousel', carouselData, { headers }),
  update: (carouselId, carouselData, headers) => axiosInstance.put(`carousel/${carouselId}`, carouselData, { headers }),
  delete: (carouselId, headers) => axiosInstance.delete(`carousel/${carouselId}`, { headers })
};

// Art API  
export const artAPI = {
  getAll: () => axiosInstance.get('art'),
  create: (artData, headers) => axiosInstance.post('art', artData, { headers }),
  update: (artId, artData, headers) => axiosInstance.put(`art/${artId}`, artData, { headers }),
  delete: (artId, headers) => axiosInstance.delete(`art/${artId}`, { headers })
};
